'use client';

import clsx from 'clsx';
import { useEffect } from 'react';

import { VersionConfigurationTab } from '@/app/evaluations/source/_versionConfigurationTab/VersionConfigurationTab';
import { Button } from '@/components/button/button';
import Modal from '@/components/modal/modal';
import { Heading } from '@/components/typography/heading';
import { useSourcePreview } from '@/hooks/queries/useSources';
import { Source } from '@/types/evals';

import styles from './modalSourceShow.module.scss';

type Props = {
  // Backwards compatibility: accept both onCloseAction and onClose
  onCloseAction: () => void;
  source: Source;
};

const noop = () => undefined;

export default function ModalSourceShow( { onCloseAction, source }: Props ) {
  // Close the modal on Escape press to match common UX expectations
  useEffect( () => {
    const onKeyDown = ( e: KeyboardEvent ) => {
      if ( e.key === 'Escape' ) {
        onCloseAction();
      }
    };

    document.addEventListener( 'keydown', onKeyDown );
    return () => document.removeEventListener( 'keydown', onKeyDown );
  }, [ onCloseAction ] );

  const previewQuery = useSourcePreview( source.id, source.version );

  return (
    <Modal
      resizable
      modalType="source-details"
      initialSize="large"
      minSize={ { width: 500, height: 300 } }
    >
      <div className={ styles.content } data-testid='source-details-modal'>
        <Heading as="h2" size="md" data-testid='source-name-title'>Source: { source.name } (v{ source.version })</Heading>
        <div className={ styles.body } data-testid='source-details-content'>
          <VersionConfigurationTab
            source={ source }
            preview={ previewQuery.data }
            previewIsLoading={ previewQuery.isLoading }
            onSourceChanged={ noop }
            onVersionChange={ noop }
            readOnly
          />
        </div>
        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
          <Button variant='primary' onClick={ onCloseAction } data-testid='source-details-close-button'>Close</Button>
        </div>
      </div>
    </Modal>
  );
}
